import {
  ResidentLeadInterestUpdateModel,
  TourAttendeeRescheduleModel,
  TourRequestCreateModel,
  TourRequestTimeUpdateModel,
} from 'src/api/models';
import { fetchResidentApplicationsService } from 'src/api/resident-application';
import { fetchApplicationStatusService } from 'src/api/resident-lead';
import { fetchSelfTourSlotsService } from 'src/api/self-tour-visit';
import {
  fetchOpenHomeToursService,
  fetchBookedToursService,
  cancelTourService,
  updateHomeInterestService,
  bookTourService,
  createResidentTourRequest,
  getResidentTourRequests,
  updateResidentTourRequest,
  cancelPrivateTour,
  rescheduleBookTourService,
  confirmBookTourService,
} from 'src/api/tours';
import useSWR from 'swr';
import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';

type HomeToursParams = {
  homeId: string;
};

export function useOpenHomeTours({ homeId }: HomeToursParams) {
  const {
    data: homeTours,
    error: homeToursError,
    mutate: mutateHomeTours,
    ...rest
  } = useSWR(`/homes/${homeId}/tours/OpenHome`, () =>
    fetchOpenHomeToursService(homeId),
  );

  return {
    homeTours,
    homeToursError,
    isHomeToursLoading: !homeToursError && !homeTours,
    mutateHomeTours,
    ...rest,
  };
}

export function useSelfTourSlots({ homeId }: HomeToursParams) {
  return useSWR(
    `/homes/${homeId}/tours/SelfTour`,
    () => fetchSelfTourSlotsService(homeId),
    { shouldRetryOnError: false },
  );
}

export function useBookedTours(
  { homeId }: HomeToursParams,
  { enabled = true }: { enabled?: boolean } = { enabled: true },
) {
  const {
    data: bookedTours,
    error: bookedToursError,
    mutate: mutateBookedTours,
    ...rest
  } = useSWR(enabled ? `/homes/${homeId}/tours/booked` : null, () =>
    fetchBookedToursService(homeId),
  );

  return {
    bookedTours,
    bookedToursError,
    isBookedToursLoading: !bookedToursError && !bookedTours,
    mutateBookedTours,
    ...rest,
  };
}

export function useBookTour(
  { homeId }: { homeId: string },
  // `SWRMutationConfiguration`, not `SWRConfiguration`: the query config does
  // not match `useSWRMutation`'s third parameter, so overload resolution fell
  // back to the no-extra-argument form and typed the trigger as taking `null`.
  options?: SWRMutationConfiguration<
    Awaited<ReturnType<typeof bookTourService>>,
    unknown,
    { tourId: string },
    string
  >,
) {
  const {
    trigger: mutateBookTour,
    isMutating: isBookTourMutating,
    ...rest
  } = useSWRMutation(
    `/homes/${homeId}/tours/booked`,
    (_url, { arg }: { arg: { tourId: string } }) => bookTourService(arg),
    options,
  );

  return {
    mutateBookTour,
    isBookTourMutating,
    ...rest,
  };
}

export function useRescheduleBookTour(
  options?: SWRMutationConfiguration<
    Awaited<ReturnType<typeof rescheduleBookTourService>>,
    unknown,
    TourAttendeeRescheduleModel,
    string
  >,
) {
  const {
    trigger: mutateReschedule,
    isMutating: isReschedulingBookTourMutating,
    ...rest
  } = useSWRMutation(
    `/tours/reschedule`,
    (_url, { arg }: { arg: TourAttendeeRescheduleModel }) =>
      rescheduleBookTourService(arg),
    options,
  );

  return {
    mutateReschedule,
    isReschedulingBookTourMutating,
    ...rest,
  };
}

export function useConfirmBookTour(
  options?: SWRMutationConfiguration<
    Awaited<ReturnType<typeof confirmBookTourService>>,
    unknown,
    { tourId: string },
    string
  >,
) {
  const {
    trigger: mutateConfirm,
    isMutating: isConfirmingBookTourMutating,
    ...rest
  } = useSWRMutation(
    `/residents/tours/confirm`,
    (_url, { arg }: { arg: { tourId: string } }) =>
      confirmBookTourService(arg.tourId),
    options,
  );

  return {
    mutateConfirm,
    isConfirmingBookTourMutating,
    ...rest,
  };
}

export function useCancelTour({
  tourId,
  homeId,
  isPrivateTour,
}: {
  tourId: string;
  homeId: string;
  isPrivateTour: boolean;
}) {
  const {
    trigger: mutateCancelTour,
    isMutating: isCancelMutating,
    ...rest
  } = useSWRMutation(`/homes/${homeId}/tours/booked`, () =>
    isPrivateTour ? cancelPrivateTour(tourId) : cancelTourService(tourId),
  );

  return {
    mutateCancelTour,
    isCancelMutating,
    ...rest,
  };
}

export function useUpdateHomeInterest({
  homeId,
  data,
}: {
  homeId: string;
  data: ResidentLeadInterestUpdateModel;
}) {
  const {
    trigger: mutateUpdateHomeInterest,
    isMutating: isHomeInterestMutating,
    ...rest
  } = useSWRMutation(`/residents/home/${homeId}/interest-level`, () =>
    updateHomeInterestService(homeId, data),
  );

  return {
    mutateUpdateHomeInterest,
    isHomeInterestMutating,
    ...rest,
  };
}

export function useGetResidentTourRequests({ enabled } = { enabled: true }) {
  return useSWR(enabled ? '/tour-requests' : null, () =>
    getResidentTourRequests(),
  );
}

export function useCreateResidentTourRequest() {
  return useSWRMutation(
    '/tour-requests',
    (_url: string, { arg }: { arg: TourRequestCreateModel }) =>
      createResidentTourRequest(arg),
  );
}

export function useUpdateResidentTourRequest(tourRequestId?: string) {
  return useSWRMutation(
    () => (tourRequestId ? `/tour-requests` : null),
    (_url: string, { arg }: { arg: TourRequestTimeUpdateModel }) =>
      updateResidentTourRequest(tourRequestId!, arg),
  );
}

export function useFetchApplicationStatus(token: string) {
  const {
    data: applicationStatus,
    error: applicationStatusError,
    ...rest
  } = useSWR(`/homes/application-status/${token}`, () =>
    fetchApplicationStatusService(token),
  );

  return {
    applicationStatus,
    applicationStatusError,
    ...rest,
  };
}

export function useResidentApplications(skip?: boolean) {
  const {
    data: applications,
    error: applicationsError,
    isLoading: isLoadingApplications,
    isValidating: isValidatingStep,
    ...rest
  } = useSWR(!skip ? '/residents/applications' : null, (_key: string) => {
    return fetchResidentApplicationsService();
  });

  return {
    applications,
    applicationsError,
    isLoadingApplications,
    isValidatingStep,
    ...rest,
  };
}
